import { Injectable, OnDestroy } from '@angular/core';
import {
  CreateHaulerCommand,
  CreateTruckListCommand,
  DeleteTruckListCommand,
  EditHaulerCommand,
  EditTruckCommand,
  IHaulerSearchViewModelTotalTypePagedResultResponse,
  IHaulerViewModel,
  ITruckSearchViewModelTotalTypePagedResultResponse,
  TotalType,
  TotalTypeITotal,
  TruckDownloadQuery,
  TruckSearchViewModel,
  TruckSearchViewModelTotalTypePagedResultResponse
} from '@services/cleanfill-api/cleanfill-api.generated';
import { ISearchQuery } from '@view-models/interfaces/search-query';
import { BehaviorSubject, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { HttpHaulersService } from './http-haulers.service';

@Injectable({
  providedIn: 'root'
})
export class StoreHaulersService implements OnDestroy {
  private haulerIdSubj$ = new BehaviorSubject<string>(null);
  haulerId$ = this.haulerIdSubj$.asObservable();
  get hauler() {
    return this.haulerSubj$.value;
  }
  get haulerId() {
    return this.haulerIdSubj$.value;
  }

  private truckRecordsSubj$ = new BehaviorSubject<ITruckSearchViewModelTotalTypePagedResultResponse>(null);
  truckList$ = this.truckRecordsSubj$.asObservable().pipe(map(v => v?.records || []));
  totalTrucks$ = this.truckRecordsSubj$.asObservable().pipe(map(v => v?.totals.find(total => total.type === TotalType.Total).value || 0));

  private haulerRecordsSubj$ = new BehaviorSubject<IHaulerSearchViewModelTotalTypePagedResultResponse>(null);
  haulerList$ = this.haulerRecordsSubj$.asObservable().pipe(map(v => v?.records || []));
  totalHauleres$ = this.haulerRecordsSubj$
    .asObservable()
    .pipe(map(v => v?.totals.find(total => total.type === TotalType.Total).value || 0));

  private haulerSubj$ = new BehaviorSubject<IHaulerViewModel>(null);
  hauler$ = this.haulerSubj$.asObservable();

  subscriptions = new Subscription();

  constructor(private httpHauler: HttpHaulersService) { }

  loadHauler(id: string) {
    const sub = this.httpHauler.getHauler(id).subscribe(hauler => this.haulerSubj$.next(hauler));
    this.subscriptions.add(sub);
  }

  loadHauleres(query: ISearchQuery): void {
    const sub = this.httpHauler
      .getHaulerRequests(query)
      .subscribe((hauleres: IHaulerSearchViewModelTotalTypePagedResultResponse) => this.haulerRecordsSubj$.next(hauleres));
    this.subscriptions.add(sub);
  }
  loadHauleresById(clientId: string, query: ISearchQuery): void {
    const sub = this.httpHauler
      .getHaulerRequestsById(clientId, query)
      .subscribe((hauleres: IHaulerSearchViewModelTotalTypePagedResultResponse) => this.haulerRecordsSubj$.next(hauleres));
    this.subscriptions.add(sub);
  }
  loadTrucks(query: ISearchQuery, companyUniqueId?: string): Promise<TruckSearchViewModel[]> {
    return new Promise(resolve => {
      const sub = this.httpHauler
        .getTrucks(query, companyUniqueId)
        .subscribe((trucks: TruckSearchViewModelTotalTypePagedResultResponse) => {
          this.truckRecordsSubj$.next(trucks);
          resolve(trucks?.records || []);
        });
      this.subscriptions.add(sub);
    });
  }
  getTrucksList(query: ISearchQuery): Promise<TruckSearchViewModel[]> {
    return new Promise(resolve => {
      const sub = this.httpHauler
        .getTrucks(query)
        .pipe(take(1))
        .subscribe((trucks: TruckSearchViewModelTotalTypePagedResultResponse) => {
          this.truckRecordsSubj$.next(trucks);
          resolve(trucks?.records || []);
        });
      this.subscriptions.add(sub);
    });
  }

  createHauler(
    payload: CreateHaulerCommand,
    fn = res => {
      return res;
    }
  ): void {
    const sub = this.httpHauler.createHauler(payload).subscribe(fn);
    this.subscriptions.add(sub);
  }
  // createTruck(
  //   payload: CreateTruckCommand,
  //   fn = truck => {
  //     return truck;
  //   }
  // ): void {
  //   const sub = this.httpHauler.createTruck(payload).subscribe(fn);
  //   this.subscriptions.add(sub);
  // }
  createTrucks(
    payload: CreateTruckListCommand,
    fn = truck => {
      return truck;
    },
    fnerr = err => {
      return err;
    }
  ): void {
    const sub = this.httpHauler.createTrucks(payload).subscribe(fn, fnerr);
    this.subscriptions.add(sub);
  }
  downloadBarcodes(body: TruckDownloadQuery) {
    return this.httpHauler.downloadBarcodes(body);
  }
  deleteTruck(uniqueId: string, fn: () => void) {
    const sub = this.httpHauler.deleteTruck(uniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }
  deleteTrucks(payload: DeleteTruckListCommand, fn: () => void) {
    const sub = this.httpHauler.deleteTrucks(payload).subscribe(fn);
    this.subscriptions.add(sub);
  }
  editTruck(payload: EditTruckCommand, fn: () => void) {
    const sub = this.httpHauler.editTruck(payload).subscribe(fn);
    this.subscriptions.add(sub);
  }
  getEmailLogs(uniqueId: string) {
    return this.httpHauler.getEmailLogs(uniqueId);
  }
  async patchTruck(truck: TruckSearchViewModel): Promise<void> {
    const queryResult = this.truckRecordsSubj$.value;
    const list = queryResult?.records.slice();
    if (!list) {
      return;
    }
    list.unshift(truck);
    const totalTrucks$ = await this.totalTrucks$.pipe(take(1)).toPromise();
    const totals = [new TotalTypeITotal({ type: TotalType.Total, value: totalTrucks$ + 1 })];
    this.truckRecordsSubj$.next({ ...queryResult, totals, records: list });
  }
  async patchDeleteTruck(truckId: string): Promise<void> {
    const queryResult = this.truckRecordsSubj$.value;
    const list = queryResult?.records.slice();
    if (!list) {
      return;
    }
    const updatedList = list.filter(t => t.uniqueId !== truckId);
    const totalTrucks$ = await this.totalTrucks$.pipe(take(1)).toPromise();
    const totals = [new TotalTypeITotal({ type: TotalType.Total, value: totalTrucks$ > 0 ? totalTrucks$ - 1 : 0 })];
    this.truckRecordsSubj$.next({ ...queryResult, totals, records: updatedList });
  }

  editHauler(payload: EditHaulerCommand, fn = () => { }) {
    const sub = this.httpHauler.editHauler(payload).subscribe(fn);
    this.subscriptions.add(sub);
  }

  clearHauleres(): void {
    this.haulerRecordsSubj$.next(null);
  }
  clearTrucks(): void {
    this.truckRecordsSubj$.next(null);
  }

  // editHauler(payload: EditHaulerCommand, fn = () => { }): void {
  //   const sub = this.subscriptions.add(this.httpHauler.editHaulerRequest(payload).subscribe(fn));
  //   this.subscriptions.add(sub);
  // }

  deleteHauler(haulerId: string, fn = () => { }): void {
    const sub = this.httpHauler.deleteHauler(haulerId).subscribe(fn);
    this.subscriptions.add(sub);
  }

  clearHauler(): void {
    this.haulerSubj$.next(null);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
